/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * AI System List Component
 * Displays all AI systems with filtering and CRUD operations
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Typography,
  Grid,
  TextField,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  FileDownload as ExportIcon,
  FileUpload as ImportIcon,
  ContentCopy as CloneIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { aiSystemApi } from '../../services/api';
import { formatStoredDate, formatStoredDateTime } from '../../utils/datetime';
import type { AISystemListItem, AISystem } from '../../types';
import { PageHeader, LoadingSpinner, ConfirmDialog } from '../common';
import { AISystemFormAdvanced } from './AISystemFormAdvanced';
import { CreateSystemWizard } from './CreateSystemWizard';

type SystemStatusFilter = 'all' | 'active' | 'inactive';
type SystemSortOrder = '-created_at' | 'created_at' | '-week_cost' | 'week_cost';
type ToggleSystemStatePayload = {
  id: number;
  isActive: boolean;
  name: string;
};

const formatCreatedDate = (value: string) =>
  formatStoredDate(value, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }, '-');

const formatDateTime = (value?: string | null) => {
  if (!value) return 'Not scheduled';
  return formatStoredDateTime(value, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }, 'Not scheduled');
};

const formatCurrency = (value?: number | null) =>
  `$${(value ?? 0).toFixed(2)}`;

const formatRunStatus = (status?: string | null) => {
  if (!status) {
    return 'No runs yet';
  }

  switch (status) {
    case 'completed':
      return 'Completed';
    case 'running':
      return 'Running';
    case 'error':
      return 'Error';
    case 'start':
      return 'Starting';
    case 'stopped':
      return 'Stopped';
    default:
      return status;
  }
};

const runStatusColor = (status?: string | null): 'success' | 'info' | 'error' | 'default' => {
  switch (status) {
    case 'completed':
      return 'success';
    case 'running':
    case 'start':
      return 'info';
    case 'error':
      return 'error';
    default:
      return 'default';
  }
};

export const AISystemList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<SystemStatusFilter>('all');
  const [sortOrder, setSortOrder] = useState<SystemSortOrder>('-created_at');
  const [formOpen, setFormOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editingSystem, setEditingSystem] = useState<AISystem | undefined>();
  const [deletingSystem, setDeletingSystem] = useState<AISystemListItem | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importData, setImportData] = useState<string>('');
  const [apiKeyMappings, setApiKeyMappings] = useState<Record<string, string>>({});

  useEffect(() => {
    if (searchParams.get('create') === '1') {
      setWizardOpen(true);
    }
  }, [searchParams]);

  const closeCreateWizard = () => {
    setWizardOpen(false);

    if (searchParams.get('create') !== '1') {
      return;
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('create');
    setSearchParams(nextParams, { replace: true });
  };

  const systemFilters = useMemo(
    () => ({
      search: searchTerm || undefined,
      is_active:
        statusFilter === 'all'
          ? undefined
          : statusFilter === 'active',
      ordering: sortOrder,
    }),
    [searchTerm, sortOrder, statusFilter],
  );

  // Fetch systems
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['systems', systemFilters],
    queryFn: () => aiSystemApi.list(systemFilters),
    refetchOnMount: 'always',
    staleTime: 0,
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: ToggleSystemStatePayload) =>
      aiSystemApi.update(id, { is_active: isActive }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['systems'] });
      toast.success(`${variables.name} ${variables.isActive ? 'activated' : 'deactivated'}`);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update system status');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => aiSystemApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['systems'] });
      queryClient.invalidateQueries({ queryKey: ['experts'] });
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      toast.success('System deleted successfully');
      setDeletingSystem(null);
    },
    onError: (error: any) => {
      console.error('Failed to delete system:', error);
      
      // If it's a 404, the system was already deleted - refresh the list
      if (error.response?.status === 404) {
        console.log('System already deleted, refreshing list...');
        queryClient.invalidateQueries({ queryKey: ['systems'] });
        queryClient.invalidateQueries({ queryKey: ['experts'] });
        queryClient.invalidateQueries({ queryKey: ['schedules'] });
        toast.info('System was already deleted. List refreshed.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to delete system');
      }
      
      setDeletingSystem(null);
    },
  });

  // Export system
  const handleExport = async (system: AISystemListItem) => {
    try {
      const exportData = await aiSystemApi.export(system.id, { mask_api_keys: true });
      
      // Create a downloadable JSON file
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { 
        type: 'application/json' 
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ai-system-${system.name.replace(/\s+/g, '-').toLowerCase()}-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('System exported successfully');
    } catch (error) {
      toast.error('Failed to export system');
      console.error('Export error:', error);
    }
  };

  // Import mutation
  const importMutation = useMutation({
    mutationFn: ({ data, mappings }: { data: Record<string, any>; mappings: Record<string, string> }) =>
      aiSystemApi.importSystem(data, mappings),
    onSuccess: async () => {
      // Clear search term to show all systems including the newly imported one
      setSearchTerm('');
      // Invalidate all systems queries
      queryClient.invalidateQueries({ queryKey: ['systems'] });
      // Force immediate refetch with empty search
      await refetch();
      toast.success('System imported successfully');
      setImportDialogOpen(false);
      setImportData('');
      setApiKeyMappings({});
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to import system');
    },
  });

  // Clone mutation
  const cloneMutation = useMutation({
    mutationFn: (id: number) => aiSystemApi.clone(id),
    onSuccess: async (response) => {
      queryClient.invalidateQueries({ queryKey: ['systems'] });
      await refetch();
      toast.success(response.message || 'System cloned successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to clone system');
    },
  });

  // Handle import
  const handleImport = async () => {
    try {
      const data = JSON.parse(importData);
      await importMutation.mutateAsync({ data, mappings: apiKeyMappings });
    } catch (error) {
      toast.error('Invalid JSON format');
    }
  };

  // Handle clone
  const handleClone = async (system: AISystemListItem) => {
    if (window.confirm(`Clone "${system.name}"? This will create a copy with all experts and connections.`)) {
      await cloneMutation.mutateAsync(system.id);
    }
  };

  const handleToggleActive = (system: AISystemListItem, isActive: boolean) => {
    toggleActiveMutation.mutate({ id: system.id, isActive, name: system.name });
  };

  // Handle file upload
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setImportData(content);
        
        // Try to parse and extract provider info for API key mapping
        try {
          const parsedData = JSON.parse(content);
          if (parsedData.llm_providers) {
            const mappings: Record<string, string> = {};
            parsedData.llm_providers.forEach((provider: any) => {
              if (provider.api_key === '***MASKED***') {
                mappings[provider.export_id] = '';
              }
            });
            setApiKeyMappings(mappings);
          }
        } catch (error) {
          console.error('Failed to parse import data:', error);
        }
      };
      reader.readAsText(file);
    }
  };

  return (
    <Box>
      <PageHeader
        title="AI Systems"
        actions={
          <Box display="flex" gap={{ xs: 0.5, sm: 1 }} flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={<ImportIcon />}
              onClick={() => setImportDialogOpen(true)}
              size="small"
              sx={{ 
                display: { xs: 'none', sm: 'flex' },
              }}
            >
              Import System
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setWizardOpen(true)}
              size="small"
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Create System</Box>
              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>New</Box>
            </Button>
          </Box>
        }
      />

      <AISystemFormAdvanced
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditingSystem(undefined);
        }}
        system={editingSystem}
      />

      <CreateSystemWizard
        open={wizardOpen}
        onClose={closeCreateWizard}
      />

      <ConfirmDialog
        open={!!deletingSystem}
        title="Delete AI System"
        message={`Are you sure you want to delete "${deletingSystem?.name}"? This will also remove all associated experts and configurations. This action cannot be undone.`}
        onConfirm={async () => {
          if (deletingSystem) {
            await deleteMutation.mutateAsync(deletingSystem.id);
          }
        }}
        onCancel={() => setDeletingSystem(null)}
      />

      {isLoading ? (
        <LoadingSpinner message="Loading systems..." />
      ) : (
        <>
          <Paper variant="outlined" sx={{ p: { xs: 1.25, sm: 1.5 }, mb: { xs: 2, sm: 3 } }}>
            <Stack
              direction={{ xs: 'column', lg: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', lg: 'center' }}
              gap={1.25}
            >
              <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                <TextField
                  placeholder="Search systems"
                  variant="outlined"
                  size="small"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{ width: { xs: '100%', sm: 280, md: 320 } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />

                <ToggleButtonGroup
                  size="small"
                  value={statusFilter}
                  exclusive
                  onChange={(_, value) => {
                    if (value) {
                      setStatusFilter(value);
                    }
                  }}
                  sx={{ flexWrap: 'wrap' }}
                >
                  <ToggleButton value="all" sx={{ textTransform: 'none' }}>All</ToggleButton>
                  <ToggleButton value="active" sx={{ textTransform: 'none' }}>Active</ToggleButton>
                  <ToggleButton value="inactive" sx={{ textTransform: 'none' }}>Inactive</ToggleButton>
                </ToggleButtonGroup>

                <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 170 } }}>
                  <InputLabel id="systems-sort-label">Sort</InputLabel>
                  <Select
                    labelId="systems-sort-label"
                    label="Sort"
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value as SystemSortOrder)}
                  >
                    <MenuItem value="-created_at">Newest first</MenuItem>
                    <MenuItem value="created_at">Oldest first</MenuItem>
                    <MenuItem value="-week_cost">Highest weekly cost</MenuItem>
                    <MenuItem value="week_cost">Lowest weekly cost</MenuItem>
                  </Select>
                </FormControl>
              </Stack>

              <Typography variant="body2" color="text.secondary">
                {data?.count ?? 0} system{(data?.count ?? 0) === 1 ? '' : 's'}
              </Typography>
            </Stack>
          </Paper>

          {/* Systems Grid */}
          <Grid container spacing={{ xs: 2.25, sm: 3.25, md: 4.25 }}>
            {data?.results.map((system: AISystemListItem) => (
              <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={system.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', border: '1px solid', borderColor: 'divider' }}>
                  <CardContent sx={{ p: { xs: 1.125, sm: 1.5 }, display: 'flex', flexDirection: 'column', gap: 1, flexGrow: 1 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                      <Box minWidth={0}>
                        <Chip
                          label={system.is_active ? 'Active' : 'Inactive'}
                          color={system.is_active ? 'success' : 'default'}
                          size="small"
                          sx={{ mb: 0.5 }}
                        />
                        <Typography
                          variant="h6"
                          component="div"
                          sx={{
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            fontWeight: 700,
                            lineHeight: 1.25,
                            wordBreak: 'break-word',
                          }}
                        >
                          {system.name}
                        </Typography>
                      </Box>

                      <Switch
                        checked={system.is_active}
                        onChange={(e) => handleToggleActive(system, e.target.checked)}
                        disabled={toggleActiveMutation.isPending}
                        inputProps={{ 'aria-label': `Toggle ${system.name}` }}
                      />
                    </Box>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        minHeight: 44,
                        fontSize: { xs: '0.82rem', sm: '0.875rem' },
                      }}
                    >
                      {system.description || 'No description added yet.'}
                    </Typography>

                    <Grid container spacing={0.75}>
                      <Grid size={{ xs: 6 }}>
                        <Paper variant="outlined" sx={{ px: 1, py: 0.75 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Today's Cost
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {formatCurrency(system.today_cost)}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Paper variant="outlined" sx={{ px: 1, py: 0.75 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            This Week
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {formatCurrency(system.week_cost)}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Paper variant="outlined" sx={{ px: 1, py: 0.75 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            Last Run
                          </Typography>
                          <Chip
                            size="small"
                            color={runStatusColor(system.last_run_status)}
                            label={formatRunStatus(system.last_run_status)}
                          />
                        </Paper>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Paper variant="outlined" sx={{ px: 1, py: 0.75 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Next Schedule
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {formatDateTime(system.next_schedule_at)}
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>

                    <Typography variant="caption" color="text.secondary">
                      Created {formatCreatedDate(system.created_at)}
                      {system.last_run_at ? ` • Last activity ${formatDateTime(system.last_run_at)}` : ''}
                    </Typography>
                  </CardContent>

                  <CardActions sx={{ px: { xs: 1.125, sm: 1.5 }, pb: { xs: 1.125, sm: 1.5 }, pt: 0, justifyContent: 'space-between', gap: 0.625, flexWrap: 'wrap' }}>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<ViewIcon fontSize="small" />}
                        onClick={() => navigate(`/ai/systems/${system.id}`)}
                      >
                        Open System
                      </Button>
                    </Stack>

                    <Stack direction="row" spacing={0.5}>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleExport(system)}
                        title="Export system"
                      >
                        <ExportIcon fontSize="small" />
                      </IconButton>

                      <IconButton
                        size="small"
                        color="secondary"
                        onClick={() => handleClone(system)}
                        disabled={cloneMutation.isPending}
                        title="Clone system"
                      >
                        <CloneIcon fontSize="small" />
                      </IconButton>

                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeletingSystem(system)}
                        disabled={deleteMutation.isPending}
                        title="Delete system"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>

          {data?.results.length === 0 && (
            <Box textAlign="center" py={{ xs: 4, sm: 8 }}>
              <Typography color="text.secondary">
                No systems found. Create your first AI system to get started!
              </Typography>
            </Box>
          )}
        </>
      )}

      {/* Import Dialog */}
      <Dialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={window.innerWidth < 600}
      >
        <DialogTitle>Import AI System</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Upload a JSON file exported from another AI system. If the export contains
              masked API keys, you'll need to provide the real keys below.
            </Alert>

            <Button
              variant="outlined"
              component="label"
              fullWidth
              sx={{ mb: 3 }}
            >
              Choose File
              <input
                type="file"
                hidden
                accept=".json"
                onChange={handleFileUpload}
              />
            </Button>

            <TextField
              label="Import Data (JSON)"
              multiline
              rows={10}
              fullWidth
              value={importData}
              onChange={(e) => setImportData(e.target.value)}
              placeholder="Paste JSON export data or upload a file..."
              sx={{ mb: 3 }}
            />

            {Object.keys(apiKeyMappings).length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  API Key Mappings (Required for masked providers)
                </Typography>
                {Object.entries(apiKeyMappings).map(([providerId, key]) => (
                  <TextField
                    key={providerId}
                    label={`API Key for ${providerId}`}
                    fullWidth
                    type="password"
                    value={key}
                    onChange={(e) =>
                      setApiKeyMappings({
                        ...apiKeyMappings,
                        [providerId]: e.target.value,
                      })
                    }
                    sx={{ mb: 2 }}
                    helperText="Leave empty to create inactive provider with masked key"
                  />
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={!importData || importMutation.isPending}
          >
            {importMutation.isPending ? 'Importing...' : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AISystemList;
